import React, { useEffect, useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  getPeriodYearOptions,
  QUARTER_OPTIONS,
} from "~/pages/Accounting/model/periodOptions";
import { Field } from "~/shared/ui/Field";

export interface IAccountingPeriodForm {
  Year: number;
  Quarter: number;
  reportDate: Date | null;
  lastDate: Date | null;
}

const QUARTER_END_DATES = ["-03-31", "-06-30", "-09-30", "-12-31"];

export const getQuarterEndDate = (year: number, quarter: number) =>
  new Date(`${year}${QUARTER_END_DATES[quarter - 1]}`);

interface AccountingPeriodFormProps {
  formId: string;
  defaultValues: IAccountingPeriodForm;
  onSubmitHandler: (data: IAccountingPeriodForm) => void;
  isEdit: boolean;
}

const AccountingPeriodForm = ({
  formId,
  defaultValues,
  onSubmitHandler,
  isEdit,
}: AccountingPeriodFormProps) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    lastDate: yup.mixed().nullable().required(t("validation.required")),
    reportDate: yup.mixed().nullable().required(t("validation.required")),
  });

  const { handleSubmit, control, watch, setValue, errors } =
    useForm<IAccountingPeriodForm>({
      defaultValues,
      resolver: yupResolver(schema),
    });

  const watchYear = watch("Year");
  const watchQuarter = watch("Quarter");

  // При смене года/квартала даты пересчитываются на конец выбранного квартала
  useEffect(() => {
    if (isEdit) return;
    const quarterEndDate = getQuarterEndDate(watchYear, watchQuarter);
    setValue("lastDate", quarterEndDate);
    setValue("reportDate", quarterEndDate);
  }, [watchYear, watchQuarter, isEdit, setValue]);

  // Год редактируемого периода может не входить в стандартный список —
  // добавляем его вручную, чтобы disabled Select показал значение
  const yearOptions = useMemo(() => {
    const options = getPeriodYearOptions();
    if (!options.some((option) => option.value === defaultValues.Year)) {
      return [
        { value: defaultValues.Year, label: String(defaultValues.Year) },
        ...options,
      ];
    }
    return options;
  }, [defaultValues.Year]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmitHandler)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="Year"
          control={control}
          label={t("accounting.Year")}
          options={yearOptions}
          error={errors["Year"]?.message}
          disabled={isEdit}
        />
        <Field
          type="select"
          name="Quarter"
          control={control}
          label={t("accounting.Quarter")}
          options={QUARTER_OPTIONS}
          error={errors["Quarter"]?.message}
          disabled={isEdit}
        />
        <Field
          type="datePicker"
          name="reportDate"
          control={control}
          label={t("accounting.reportDate")}
          error={errors["reportDate"]?.message}
          format="DD.MM.YYYY"
        />
        <Field
          type="datePicker"
          name="lastDate"
          control={control}
          label={t("accounting.lastDate")}
          error={errors["lastDate"]?.message}
          format="DD.MM.YYYY"
        />
      </Flex>
    </form>
  );
};

export default AccountingPeriodForm;
